import type { Discipline } from '@structural-climbing/validation'
import type { NavGroupId } from './navGroups'

/**
 * One icon per tab, drawn rather than pulled from an icon package.
 *
 * Four shapes do not justify a dependency, and these are tuned to sit on the
 * same optical weight as Inter at 11px, which an off-the-shelf set would not be.
 */
export function NavGlyph({ group, className = '' }: { group: NavGroupId; className?: string }) {
  const common = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }

  switch (group) {
    case 'home':
      return (
        <svg {...common}>
          <path d="M3.5 10.5 12 4l8.5 6.5V19a1 1 0 0 1-1 1h-5v-5.5h-5V20h-5a1 1 0 0 1-1-1z" />
        </svg>
      )
    // A climber's traverse: holds scattered up a wall.
    case 'climb':
      return (
        <svg {...common}>
          <circle cx="7" cy="17.5" r="1.6" />
          <circle cx="11.5" cy="11" r="1.6" />
          <circle cx="8.5" cy="6" r="1.6" />
          <circle cx="17" cy="14" r="1.6" />
          <circle cx="16.5" cy="5.5" r="1.6" />
        </svg>
      )
    case 'community':
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="3" />
          <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
          <path d="M16 6.6a3 3 0 0 1 0 5.8" />
          <path d="M17.5 15a5.5 5.5 0 0 1 3 4.5" />
        </svg>
      )
    case 'me':
      return (
        <svg {...common}>
          <circle cx="12" cy="8.5" r="3.5" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      )
  }
}

/** The two walls under Climb, drawn to sit beside the group glyphs. */
export function DisciplineGlyph({
  discipline,
  className = '',
}: {
  discipline: Discipline
  className?: string
}) {
  const common = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }

  // A boulder: one squat block of rock on the ground.
  if (discipline === 'boulder') {
    return (
      <svg {...common}>
        <path d="M3 19.5h18" />
        <path d="M5 19.5 6.5 11 11 6.5l5.5 1.5 3 5.5-.5 6" />
      </svg>
    )
  }
  // Lead: the rope running up through clipped bolts.
  return (
    <svg {...common}>
      <circle cx="8" cy="5.5" r="1.6" />
      <circle cx="15" cy="11.5" r="1.6" />
      <circle cx="9" cy="17.5" r="1.6" />
      <path d="M12 21.5 9 17.5l6-6-7-6L12 2.5" />
    </svg>
  )
}
